import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    const raw = localStorage.getItem("k12_user");
    return raw ? JSON.parse(raw) : null;
  });
  const [loading, setLoading] = useState(true);

  const persist = useCallback((u, token) => {
    if (u) localStorage.setItem("k12_user", JSON.stringify(u));
    else localStorage.removeItem("k12_user");
    if (token) localStorage.setItem("k12_token", token);
    setUser(u);
  }, []);

  const checkAuth = useCallback(async () => {
    try {
      const res = await api.get("/auth/me");
      persist(res.data, null);
    } catch (err) {
      // On 401 (banned or invalid session), always clear local auth so the user browses as guest
      if (err?.response?.status === 401) {
        localStorage.removeItem("k12_token");
        localStorage.removeItem("k12_user");
        setUser(null);
      } else if (!localStorage.getItem("k12_token")) {
        persist(null, null);
      }
    } finally {
      setLoading(false);
    }
  }, [persist]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const res = await api.post("/auth/login", { email, password });
    persist(res.data.user, res.data.token);
    return res.data.user;
  };

  const register = async (email, password, name) => {
    const res = await api.post("/auth/register", { email, password, name });
    persist(res.data.user, res.data.token);
    return res.data.user;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    localStorage.removeItem("k12_token");
    localStorage.removeItem("k12_user");
    setUser(null);
  };

  return (
    <AuthCtx.Provider value={{ user, loading, login, register, logout, setUser, persist, checkAuth }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
